import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import type {
  ReactElement,
} from 'react';

import styled from 'styled-components';

import { Header } from '../Header';
import { Sidebar } from '../Sidebar';
import { LayoutContext } from './LayoutContext';

import { HeaderLeftBlockContext } from './HeaderLeftBlockContext';
import { useHeaderLeftBlock } from './useHeaderLeftBlock';

import type {
  LayoutProps,
} from './types';

export const StyledLayout = styled.div<{
  isCollapsible: boolean;
  isMenuCollapsed: boolean;
}>(({
  isCollapsible,
  isMenuCollapsed,

  theme: {
    grid,
    layout,
  },
}) => ({
  paddingTop: layout.HEADER_HEIGHT,
  paddingLeft: 0,

  [`@media (min-width: ${grid.SCREEN_WIDTH_MD}px)`]: {
    paddingLeft: isCollapsible
      ? isMenuCollapsed
        ? layout.SIDEBAR_WIDTH_COLLAPSED
        : layout.SIDEBAR_WIDTH
      : 0,
  },
}));

export const StyledSidebar = styled.div<{
  isOpen: boolean;
}>(({
  isOpen,
}) => ({
  position: 'relative',
  zIndex: isOpen ? 5 : 2,
}));

export const StyledBackdrop = styled.button({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  margin: 0,
  padding: 0,
  border: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
});

const StyledHeader = styled.div({
  position: 'relative',
  zIndex: 3,
});

const StyledContent = styled.div({
  position: 'relative',
  zIndex: 1,
});

export function Layout({
  menuCollapsed: menuCollapsedProp = false,

  header,
  sidebar,
  collapsible = true,
  openedGroups = undefined,

  counters = undefined,
  countersMaxNumber = undefined,

  children,

  onCollapseMenu = undefined,
  onToggleGroup = undefined,
}: LayoutProps): ReactElement {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [menuCollapsed, setMenuCollapsedState] = useState<boolean>(menuCollapsedProp);

  const openMenu = useCallback(() => {
    setCollapsed(false);
  }, []);

  const closeMenu = useCallback(() => {
    setCollapsed(true);
  }, []);

  const setMenuCollapsed = useCallback((nextMenuCollapsed: boolean): void => {
    setMenuCollapsedState(nextMenuCollapsed);

    if (onCollapseMenu) {
      onCollapseMenu(nextMenuCollapsed);
    }
  }, [
    onCollapseMenu,
  ]);

  const layoutContextValue = useMemo(() => ({
    menuCollapsed,
  }), [menuCollapsed]);

  const renderedChildren = children(layoutContextValue);

  const {
    renderedBlock: headerLeftBlock,
    contextValue: headerLeftBlockContextValue,
  } = useHeaderLeftBlock();

  return (
    <StyledLayout
      isCollapsible={collapsible}
      isMenuCollapsed={menuCollapsed}
    >
      {
        !collapsed && (
          <StyledBackdrop
            type="button"
            onClick={closeMenu}
          />
        )
      }

      <StyledSidebar
        isOpen={collapsible && !collapsed}
      >
        <Sidebar
          openedGroups={openedGroups}
          counters={counters}
          countersMaxNumber={countersMaxNumber}
          {...sidebar}
          menuCollapsed={menuCollapsed}
          collapsed={collapsed}
          collapsible={collapsible}
          closeMenu={closeMenu}
          setMenuCollapsed={setMenuCollapsed}
          onToggleGroup={onToggleGroup}
        />
      </StyledSidebar>

      <StyledHeader>
        <Header
          counters={counters}
          countersMaxNumber={countersMaxNumber}
          {...header}
          leftBlock={headerLeftBlock}
          menuCollapsed={menuCollapsed}
          collapsible={collapsible}
          openMenu={openMenu}
        />
      </StyledHeader>

      <StyledContent>
        <LayoutContext.Provider
          value={layoutContextValue}
        >
          <HeaderLeftBlockContext.Provider
            value={headerLeftBlockContextValue}
          >
            {renderedChildren}
          </HeaderLeftBlockContext.Provider>
        </LayoutContext.Provider>
      </StyledContent>
    </StyledLayout>
  );
}
